import { useEffect, useState } from "react";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import { Plus, Smartphone } from "lucide-react";

export default function Devices() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", kind: "tablet", mode: "shared" });

  const load = () => wfAPI.devices().then(setItems).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.name) { toast.error("Code and name required"); return; }
    try { await wfAPI.createDevice(form); setCreating(false); setForm({ code:"", name:"", kind:"tablet", mode:"shared" }); load(); toast.success("Device registered"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="wf-devices">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[18px] font-semibold text-hocp-navy">Devices</h2>
          <p className="text-[12.5px] text-slate-500">Every permission decision considers device + policy.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="devices-create"
          className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Register Device
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="col-span-full bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400 text-[13px]">
            No devices registered yet.
          </div>
        ) : items.map((d) => (
          <div key={d.id} data-testid={`device-${d.id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-slate-600" strokeWidth={1.75}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-hocp-navy truncate">{d.name}</div>
                <div className="text-[11.5px] text-slate-400">{d.code} · {d.kind} · {d.mode}</div>
              </div>
              <span className={`text-[10.5px] px-1.5 py-0.5 rounded ${d.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.status}</span>
            </div>
            <div className="text-[12px] text-slate-500">
              {d.paired_employee_ids?.length ? `${d.paired_employee_ids.length} paired employees` : "Unpaired"}
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e)=>e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-display font-semibold text-hocp-navy">Register Device</h3></div>
            <div className="p-5 space-y-3">
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Code</span>
                <input placeholder="e.g. fd-tablet-01" data-testid="new-device-code" value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} className={inputCls}/></label>
              <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Display name</span>
                <input placeholder="e.g. Front Desk Tablet" data-testid="new-device-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className={inputCls}/></label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Device type</span>
                  <select value={form.kind} onChange={(e)=>setForm({...form, kind:e.target.value})} className={inputCls}>
                    {["mobile","tablet","pos","kiosk","desktop"].map((k) => <option key={k}>{k}</option>)}
                  </select></label>
                <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Mode</span>
                  <select value={form.mode} onChange={(e)=>setForm({...form, mode:e.target.value})} className={inputCls}>
                    {["shared","assigned","trusted","kiosk","pos","mobile"].map((m) => <option key={m}>{m}</option>)}
                  </select></label>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button onClick={()=>setCreating(false)} className="h-9 px-3 rounded-lg text-slate-500 text-[13px] hover:bg-slate-100">Cancel</button>
              <button onClick={create} data-testid="new-device-submit" className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium">Register</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const inputCls = "w-full h-9 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
